/**
 * Data layer for `lead-ownership-transfer-tracking-sold-to-tracking`. Thin typed fetch wrappers over /api/v1/lead-ownership-transfer-tracking-sold-to-tracking.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface LeadOwnershipTransferTrackingSoldToTrackingRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListLeadOwnershipTransferTrackingSoldToTrackingResponse {
	count: number;
	items: LeadOwnershipTransferTrackingSoldToTrackingRow[];
}

export interface CreateLeadOwnershipTransferTrackingSoldToTrackingBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/lead-ownership-transfer-tracking-sold-to-tracking";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListLeadOwnershipTransferTrackingSoldToTrackingResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListLeadOwnershipTransferTrackingSoldToTrackingResponse>(res);
}

export async function create(body: CreateLeadOwnershipTransferTrackingSoldToTrackingBody): Promise<LeadOwnershipTransferTrackingSoldToTrackingRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<LeadOwnershipTransferTrackingSoldToTrackingRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const leadOwnershipTransferTrackingSoldToTrackingQueryKeys = {
	all: ["lead-ownership-transfer-tracking-sold-to-tracking"] as const,
	list: () => [...leadOwnershipTransferTrackingSoldToTrackingQueryKeys.all] as const,
	detail: (id: string) => [...leadOwnershipTransferTrackingSoldToTrackingQueryKeys.all, id] as const,
};
